import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import { isApiError } from '@platform/api-client';
import {
  Alert,
  brandingIssuesToErrors,
  Card,
  Spinner,
  useToast,
  type BrandingFormErrors,
  type BrandUpload,
} from '@platform/ui';

import { BrandingTab } from '../../../features/tenants/branding-tab';
import { IdentitySection } from '../../../features/tenants/identity-section';
import { useRefreshTenant, useTenantDetail } from '../../../features/tenants/use-tenant';
import {
  PLATFORM_ENDPOINT_PERMISSIONS,
  type SaveBrandingRequest,
  type UpdateTenantRequest,
} from '../../../lib/api/platform';
import { usePlatformClient } from '../../../lib/api/platform-client-context';
import { usePermissions } from '../../../lib/session/permissions';

export const Route = createFileRoute('/tenants/$tenantId/branding')({
  component: TenantBrandingPage,
});

type IdentityField = 'name' | 'timeZone' | 'statesServed';

/** Per-field refusals from a `VALIDATION_FAILED`, and the sentence for anything else. */
function splitIdentityError(cause: Error): {
  fields: Partial<Record<IdentityField, string>>;
  message: string | undefined;
} {
  const fields: Partial<Record<IdentityField, string>> = {};
  const other: string[] = [];
  for (const issue of isApiError(cause) ? cause.issues : []) {
    const head = issue.path[0];
    if (head === 'name' || head === 'timeZone' || head === 'statesServed') {
      fields[head] ??= issue.message;
    } else {
      other.push(issue.message);
    }
  }
  const hasFields = Object.keys(fields).length > 0;
  return {
    fields,
    message: hasFields && other.length === 0 ? undefined : [cause.message, ...other].join(' '),
  };
}

/**
 * `/tenants/$id/branding` — identity and branding (M17.2, FR-PLAT-03, FR-UI-03).
 *
 * Two saves, because they are two endpoints with two audit rows: `PATCH
 * /platform/tenants/:id` for the name, zone and states, `PUT …/branding` for the brand.
 * Uploads go through `POST …/files/upload-url` → the storage PUT → `…/complete`
 * (`client.uploadTenantFile`). All of it needs `platform.tenant.manage`; without it — and
 * for an archived clinic — both forms render read-only. Display only: the server checks
 * the same key (rules §17.2).
 */
function TenantBrandingPage() {
  const { tenantId } = Route.useParams();
  const client = usePlatformClient();
  const refresh = useRefreshTenant(tenantId);
  const { toast } = useToast();
  const { can } = usePermissions();
  const tenant = useTenantDetail(tenantId);

  const [brandingError, setBrandingError] = useState<string | undefined>(undefined);
  const [brandingFields, setBrandingFields] = useState<BrandingFormErrors | undefined>(undefined);
  const [identityError, setIdentityError] = useState<string | undefined>(undefined);
  const [identityFields, setIdentityFields] = useState<
    Partial<Record<IdentityField, string>> | undefined
  >(undefined);

  const upload = useCallback<BrandUpload>(
    (kind, file) => client.uploadTenantFile(tenantId, kind, file),
    [client, tenantId],
  );

  const saveBranding = useMutation({
    mutationFn: (input: SaveBrandingRequest) => client.saveBranding(tenantId, input),
    onMutate: () => {
      setBrandingError(undefined);
      setBrandingFields(undefined);
    },
    onSuccess: async () => {
      toast({
        tone: 'success',
        message: 'Branding saved. The clinic’s apps pick it up within a minute.',
      });
      await refresh();
    },
    onError: (cause: Error) => {
      const { errors, other } = brandingIssuesToErrors(isApiError(cause) ? cause.issues : []);
      const hasFields = Object.keys(errors).length > 0;
      setBrandingFields(hasFields ? errors : undefined);
      setBrandingError(
        hasFields && other.length === 0 ? undefined : [cause.message, ...other].join(' '),
      );
    },
  });

  const saveIdentity = useMutation({
    mutationFn: (patch: UpdateTenantRequest) => client.updateTenant(tenantId, patch),
    onMutate: () => {
      setIdentityError(undefined);
      setIdentityFields(undefined);
    },
    onSuccess: async () => {
      toast({ tone: 'success', message: 'Identity saved.' });
      await refresh();
    },
    onError: (cause: Error) => {
      const { fields, message } = splitIdentityError(cause);
      setIdentityFields(Object.keys(fields).length > 0 ? fields : undefined);
      setIdentityError(message);
    },
  });

  if (tenant.isPending) {
    return (
      <Card className="p-6">
        <Spinner label="Loading branding" />
      </Card>
    );
  }
  if (tenant.isError) {
    return <Alert tone="error">That tenant could not be loaded.</Alert>;
  }

  const summary = tenant.data.tenant;
  const readOnly =
    summary.status === 'archived' || !can(PLATFORM_ENDPOINT_PERMISSIONS.tenantManage);

  return (
    <div className="flex flex-col gap-6">
      <IdentitySection
        /* Remounts on another clinic or a saved answer, which is what resets the draft. */
        key={`${tenantId}-${summary.name}-${summary.timeZone}-${summary.statesServed.join(',')}`}
        tenant={summary}
        saving={saveIdentity.isPending}
        error={identityError}
        fieldErrors={identityFields}
        readOnly={readOnly}
        onSave={(patch) => {
          saveIdentity.mutate(patch);
        }}
      />
      <BrandingTab
        key={`${tenantId}-${JSON.stringify(tenant.data.branding)}`}
        branding={tenant.data.branding}
        upload={upload}
        error={brandingError}
        fieldErrors={brandingFields}
        saving={saveBranding.isPending}
        readOnly={readOnly}
        onSave={(input) => {
          saveBranding.mutate(input);
        }}
      />
    </div>
  );
}
